import {
  ItemsResponse,
  parseErrorContainer,
  type JsonHelper,
} from "../../../tastyworks"
import {
  ADMIN_NOTES_DESER_PARSER,
  type AdminNotes,
} from "../../../tastyworks/admin/admin-notes"
import type HttpClient from "../../../tastyworks/http"

export class AdminNotesService {
  private readonly adminNotesClient: HttpClient

  constructor(adminNotesClient: HttpClient) {
    this.adminNotesClient = adminNotesClient.nested("accounts")
  }

  readonly index = async (
    accountNumber: string,
    excludeTypes: string[]
  ): Promise<ItemsResponse<AdminNotes>> =>
    this.adminNotesClient
      .nested(accountNumber)
      .get("notes", { excludeTypes })
      .then((res) => this.populateResult(res))

  private readonly populateResult = (
    helper: JsonHelper
  ): ItemsResponse<AdminNotes> => {
    const response = new ItemsResponse<AdminNotes>()

    parseErrorContainer(helper, response)

    if (!helper.hasField("data")) {
      return response
    }

    const data = helper.getChild("data")
    response.items = data.parseArray("items", ADMIN_NOTES_DESER_PARSER)

    return response
  }

  readonly createNewNote = async (
    accountNumber: string,
    subject: string,
    type: string,
    subType: string,
    body: string
  ): Promise<ItemsResponse<AdminNotes>> => {
    const helper = await this.adminNotesClient.nested(accountNumber).postJson(
      "/notes",
      {
        body,
        subject,
        "note-type": type,
        "note-subtype": subType,
      },
      {}
    )
    const response = new ItemsResponse<AdminNotes>()
    parseErrorContainer(helper, response)

    if (!helper.hasField("data")) {
      return response
    }
    const data = helper.getChild("data")
    response.items = data.parseArray("items", ADMIN_NOTES_DESER_PARSER)
    return response
  }

  readonly updateNote = async (
    accountNumber: string,
    note: AdminNotes
  ): Promise<ItemsResponse<AdminNotes>> => {
    const { id, body, subject, noteType, subType } = note
    const payload = {
      body,
      subject,
      "note-type": noteType,
      "note-subtype": subType,
    }
    const helper = await this.adminNotesClient
      .nested(accountNumber)
      .put(`/notes/${id}`, payload)
    const response = new ItemsResponse<AdminNotes>()
    parseErrorContainer(helper, response)
    return response
  }

  readonly deleteNote = async (
    accountNumber: string,
    note: AdminNotes
  ): Promise<ItemsResponse<AdminNotes>> => {
    const helper = await this.adminNotesClient
      .nested(accountNumber)
      .doDelete(`/notes/${note.id}`)
    const response = new ItemsResponse<AdminNotes>()
    parseErrorContainer(helper, response)
    return response
  }
}
